// @flow
import * as React from 'react';
import type { MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import Modal from '../../common/modal';
import { formActions, mergeActionsToProps } from 'redux-pure-form';

import {
  applyTimezone,
  getCustomFields,
  getParamsForEventAction,
  getParamsString,
  paramsToHash,
  parseUrlParams
} from '../calendar.model.js';

import {
  checkEventParams,
  checkInStart,
  checkOutStart,
  getAircraft,
  getDiscrepancy,
  getEvent,
  getSimulator
} from '../calendar-day.actions.js';

import UIMessage from './components/ui.messages';

import { getInstructorInfo } from '../../public-schedule/schedule.actions.js';
import { selectIsNoChangesInEvents, selectUIMessages } from '../calendar.selectors.js';
import { setMessages, setModal } from '../../ui/ui.actions.js';

import EventModalHeader from './calendar.event-modal-header.js';
import EventModalFooter from './calendar.event-modal-footer.js';

import EventAdd from './calendar.event-add.js';
import EventView from './calendar.event-view.js';
import EventEdit from './calendar.event-edit.js';
import EventResource from './calendar.event-resource.js';
import EventCheckin from './calendar.event-checkin.js';
import EventCheckout from './calendar.event-checkout.js';
import EventCancel from './calendar.event-cancel.js';
import EventRecurring from './calendar.event-recurring.js';

import DiscrepancyAdd from './calendar.discrepancy-add.js';
import DiscrepancyView from './calendar.discrepancy-view.js';

import InstructorSchedule from '../../public-schedule/schedule.instructor.js';

import WeightAndBalance from '../weight-and-balance/weight-and-balance.js';
import PassengerModal from '../weight-and-balance/passenger/passenger.modal';

import {
  defaultEventForm,
  DISCREPANCY_ADD,
  DISCREPANCY_VIEW,
  EVENT_ADD,
  EVENT_CANCEL,
  EVENT_CHECKIN,
  EVENT_CHECKOUT,
  EVENT_EDIT,
  EVENT_RECURRING,
  EVENT_RESOURCE,
  EVENT_VIEW,
  INSTRUCTORS_SCHEDULE
} from '../calendar.consts.js';
import {
  ADD_PASSENGER,
  EDIT_PASSENGER,
  WEIGHT_AND_BALANCE
} from '../weight-and-balance/weight-and-balance.consts';
import { isUserInstructor } from './utils/permissions';

type OwnProps = {|
  getEvent: Function,
  checkEventParams: Function,
  checkOutStart: Function,
  checkInStart: Function,
  getAircraft: Function,
  getSimulator: Function,
  getDiscrepancy: Function
|};

type MappedState = {|
  UI: $ElementType<ReduxState, 'UI'>,
  URL: $ElementType<ReduxState, 'URL'>,
  filterForm: $ElementType<ReduxState, 'filterForm'>,
  timezones: $ElementType<ReduxState, 'timezones'>,
  timezone: $ElementType<ReduxState, 'timezone'>,
  locations: $ElementType<ReduxState, 'locations'>,
  locationsList: $ElementType<ReduxState, 'locationsList'>,
  eventForm: $ElementType<ReduxState, 'eventForm'>,
  user: $ElementType<ReduxState, 'user'>,
  events: $ElementType<ReduxState, 'events'>,
  resources: $ElementType<ReduxState, 'resources'>,
  resourcesList: $ElementType<ReduxState, 'resourcesList'>,
  customFields: $ElementType<ReduxState, 'customFields'>,
  customFieldsList: $ElementType<ReduxState, 'customFieldsList'>
|};

type Props = {|
  ...OwnProps,
  ...MappedState
|};

function getActiveModal(action) {
  switch (action) {
    case EVENT_ADD:
      return {
        name: 'EventAdd',
        EventAdd: EventAdd
      };
    case EVENT_VIEW:
      return {
        name: 'EventView',
        EventView: EventView
      };
    case EVENT_EDIT:
      return {
        name: 'EventEdit',
        EventEdit: EventEdit
      };
    case EVENT_RESOURCE:
      return {
        name: 'EventResource',
        EventResource: EventResource
      };
    case EVENT_CHECKIN:
      return {
        name: 'EventCheckin',
        EventCheckin: EventCheckin
      };
    case EVENT_CHECKOUT:
      return {
        name: 'EventCheckout',
        EventCheckout: EventCheckout
      };
    case EVENT_CANCEL:
      return {
        name: 'EventCancel',
        EventCancel: EventCancel
      };
    case EVENT_RECURRING:
      return {
        name: 'EventRecurring',
        EventRecurring: EventRecurring
      };
    case DISCREPANCY_ADD:
      return {
        name: 'DiscrepancyAdd',
        DiscrepancyAdd: DiscrepancyAdd
      };
    case DISCREPANCY_VIEW:
      return {
        name: 'DiscrepancyView',
        DiscrepancyView: DiscrepancyView
      };
    case INSTRUCTORS_SCHEDULE:
      return {
        name: 'InstructorSchedule',
        InstructorSchedule: InstructorSchedule
      };
    case WEIGHT_AND_BALANCE:
      return {
        name: 'WeightAndBalance',
        WeightAndBalance: WeightAndBalance,
        className: 'calculate-passenger-modal'
      };
    case ADD_PASSENGER:
    case EDIT_PASSENGER:
      return {
        name: 'PassengerModal',
        PassengerModal: PassengerModal,
        className: 'calculate-passenger-modal'
      };
    default:
      return null;
  }
}

class EventModals extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.switchModals = this.switchModals.bind(this);
    this.initModal = this.initModal.bind(this);
  }

  componentWillMount() {
    window.scrollTo(0, this.props.scrollY);
    this.props.setMessages([]);

    if (this.props.URL.eventParams.action) {
      return this.initModal(this.props);
    }

    return this.props.handleHistoryChange(false);
  }

  componentWillUnmount() {
    Object.keys(this.props.UI.modals).forEach(key => {
      const match = /^counter\-(\d*)/.exec(key);

      if (match && match.length) return this.props.setModal(false, key);
    });
    window.scrollTo(0, this.props.scrollY);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.URL.eventParams.action !== nextProps.URL.eventParams.action) {
      this.initModal(nextProps, this.props.URL.eventParams.action);
    }

    if (this.props.event !== nextProps.event && this.props.URL.eventParams.action === EVENT_EDIT) {
      this.props.checkEventParams();
    }
  }

  handleModalClose() {
    let canClose = true;
    if (this.props.URL.eventParams.action === EVENT_EDIT && !this.props.isNoChangesInEvents) {
      canClose = confirm('Are you sure you want to close without saving?');
    }

    if (canClose) {
      this.props.setModal(false, 'hideMessages');
      this.props.fieldAttrs.onChange({ eventForm: defaultEventForm });

      return this.props.paramsToHash({}, this.props.scrollY);
    }

    return canClose;
  }

  initModal(props, prevAction) {
    const params = parseUrlParams();
    const { URL, timezones, locations, locationsList, user, eventForm, resources } = props;

    this.props.handleHistoryChange();

    const resource = resources[params.resource];

    const timezone =
      params.location && locations[params.location]
        ? timezones[locations[params.location].timeZoneID]
        : {};

    switch (params.action) {
      case EVENT_ADD:
        let nextEventFrom;

        if (prevAction === EVENT_RECURRING && eventForm.startTime) {
          nextEventFrom = {
            ...eventForm
          };
        } else {
          nextEventFrom = {
            ...defaultEventForm,
            ...eventForm,
            actionName: URL.action,
            organizationUnique: params.org,
            startTime: params.startTime,
            finishTime:
              params.finishTime ||
              parseInt(params.startTime, 10) + parseInt(user.eventDefaultLength, 10),
            globalStartTime: applyTimezone(params.startTime, props.timezone.offset),
            globalFinishTime: applyTimezone(
              params.finishTime ||
                parseInt(params.startTime, 10) + parseInt(user.eventDefaultLength, 10),
              props.timezone.offset
            ),
            instructorID:
              resource && resource.resourceTypeName === 'Instructor' ? params.resource : '',
            resources:
              resource && resource.resourceTypeName !== 'Instructor' ? [params.resource] : [],
            locationID:
              params.location ||
              locationsList.find(id => locations[id].organizationUnique === params.org),
            params: getParamsString(props.filterForm, props.userFilters[props.filterForm.id]),
            customFields: getCustomFields(props.customFieldsList, props.customFields, params.org),
            _timezone: props.timezone.id
          };
        }

        const userInstructorResourceId = isUserInstructor(user, resources);
        const instructorResource = resources[userInstructorResourceId];

        if (
          userInstructorResourceId &&
          instructorResource &&
          instructorResource.published === 1 &&
          instructorResource.locationID === nextEventFrom.locationID
        ) {
          nextEventFrom.instructorID = userInstructorResourceId;
        }

        props.fieldAttrs.onChange({ eventForm: nextEventFrom });

        return props.checkEventParams(true);

      case EVENT_EDIT:
        let nextEditEventFrom = {
          ...eventForm,
          recurringAction: 'save'
        };

        if (prevAction === EVENT_RECURRING && eventForm.startTime) {
          if (!props.event || !props.event.id) {
            nextEditEventFrom = {
              ...props.event,
              ...nextEditEventFrom,
              resources: props.event.resources.map(res => res.id)
            };
          }
        } else {
          if (!props.event || !props.event.id) {
            props.getEvent();

            nextEditEventFrom = {
              ...defaultEventForm,
              ...nextEditEventFrom,
              actionName: URL.action,
              startTime: params.startTime,
              finishTime: params.finishTime,
              globalStartTime: applyTimezone(params.startTime, props.timezone.offset),
              globalFinishTime: applyTimezone(params.finishTime, props.timezone.offset),
              instructorID:
                resource && resource.resourceTypeName === 'Instructor' ? params.resource : '',
              resources:
                resource && resource.resourceTypeName !== 'Instructor' ? [params.resource] : [],
              locationID: params.location,
              recurringEndDate: eventForm ? eventForm.recurringEndDate || '' : '',
              recurring: eventForm ? eventForm.recurring || '0' : '0',
              customFields: getCustomFields(props.customFieldsList, props.customFields, params.org),
              params: getParamsString(props.filterForm, props.userFilters[props.filterForm.id]),
              _timezone: props.timezone.id
            };
          } else {
            nextEditEventFrom = {
              ...defaultEventForm,
              ...nextEditEventFrom,
              ...props.event,
              actionName: URL.action,
              globalStartTime: applyTimezone(props.event.startTime, props.timezone.offset),
              globalFinishTime: applyTimezone(props.event.finishTime, props.timezone.offset),
              recurringEndDate: props.event.recurringEndDate || '',
              resources: props.event.resources.map(res => res.id),
              params: getParamsString(props.filterForm, props.userFilters[props.filterForm.id]),
              _timezone: props.timezone.id
            };
          }
        }

        if (nextEditEventFrom.recurring === '1') {
          nextEditEventFrom = {
            ...nextEditEventFrom,
            eventsForAction: 'following'
          };
        }

        props.fieldAttrs.onChange({ eventForm: nextEditEventFrom });
        return props.checkEventParams();

      case EVENT_RECURRING:
        return props.fieldAttrs.onChange('eventForm.recurring', '1');

      case EVENT_VIEW:
        return props.getEvent();

      case EVENT_CANCEL:
        if (!props.event || !props.event.pilotID) props.getEvent();
        return true;

      case EVENT_CHECKOUT:
        if (!props.event || !props.event.pilotID) props.getEvent();

        const res = props.resourcesList.find(
          id => URL.eventParams.element === props.resources[id].aircraftID
        );

        if (res) {
          props.getAircraft();
        }

        return props.checkOutStart();

      case EVENT_CHECKIN:
        if (!props.event || !props.event.pilotID) props.getEvent();
        return props.checkInStart();

      case DISCREPANCY_VIEW:
        if (!props.discrepancy || !props.discrepancy.id) props.getDiscrepancy();
        if (!props.event || !props.event.pilotID) props.getEvent();
        return true;

      case DISCREPANCY_ADD:
      case EVENT_RESOURCE:
        if (!props.event || !props.event.pilotID) props.getEvent();
        if (!props.aircraft || !props.aircraft.id) {
          const res = props.resourcesList.find(
            id => URL.eventParams.element === props.resources[id].aircraftID
          );
          if (res) {
            return props.getAircraft();
          }
          return props.getSimulator();
        }

      case INSTRUCTORS_SCHEDULE:
        if (!props.instructor || !props.instructor.id) {
          props.getInstructorInfo(URL.eventParams.instructor, URL.eventParams.orgId);
        }
        return true;
    }
  }

  switchModals(
    action,
    params = this.props.URL.eventParams,
    nextBreadcrumbs = this.props.URL.eventBreadcrumbs,
    aliases
  ) {
    this.props.fieldAttrs.onChange('URL.eventBreadcrumbs', nextBreadcrumbs);

    const paramsForEventAction = getParamsForEventAction(action, params, aliases);

    return this.props.paramsToHash(paramsForEventAction, this.props.scrollY);
  }

  getBalanceStatus = () => {
    const { weightAndBalance } = this.props;
    const table = weightAndBalance.weightAndBalanceTable;
    const errorArray = [];

    Object.keys(table).forEach(key => {
      if (table[key] && table[key].status && table[key].status.length) {
        errorArray.push(table[key].status[0]);
      }
    });

    return errorArray.length;
  };

  render() {
    const { UI, URL, setModal, UIMessages } = this.props;

    if (!URL.eventParams.action) return null;

    const balanceStatus = this.getBalanceStatus();

    const activeModal = getActiveModal(URL.eventParams.action);
    const ActiveComponent = activeModal[activeModal.name];
    const isDraggable = !/Mobi/.test(window.navigator.userAgent);
    const modalClassName = `row smart-dialog-day smart-dialog event-modal-wrap ${activeModal.className ||
      ''}`.trim();

    return (
      <div className="event-modal-container">
        <Modal
          isOpen
          draggable={isDraggable}
          cancelBackdropClick={true}
          className={modalClassName}
          onClose={this.handleModalClose}
          header={<EventModalHeader switchModals={this.switchModals} action={URL} />}
          footer={
            ActiveComponent.footer ? (
              <div>
                {!!UIMessages.length && (
                  <UIMessage
                    onHide={setModal.bind(this, true, 'hideMessages')}
                    onShow={setModal.bind(this, false, 'hideMessages')}
                    messages={UIMessages}
                    UI={UI}
                  />
                )}
                <EventModalFooter balanceStatus={balanceStatus} />
              </div>
            ) : null
          }
        >
          <ActiveComponent switchModals={this.switchModals} URL={URL} />
        </Modal>
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<ReduxState, OwnProps, MappedState> = state => ({
  UIMessages: selectUIMessages(state.URL.eventParams.action)(state),
  UI: state.UI,
  URL: state.URL,
  filterForm: state.filterForm,
  userFilters: state.userFilters,
  timezones: state.timezones,
  timezone: state.timezone,
  locations: state.locations,
  locationsList: state.locationsList,
  eventForm: state.eventForm,
  user: state.user,
  events: state.events,
  event: state.event,
  resources: state.resources,
  resourcesList: state.resourcesList,
  customFields: state.customFields,
  customFieldsList: state.customFieldsList,
  scrollY: state.userUI.scrollY,
  isNoChangesInEvents: selectIsNoChangesInEvents(state),
  weightAndBalance: state.weightAndBalance
});

export default connect(
  mapStateToProps,
  {
    setModal,
    setMessages,
    paramsToHash,
    getAircraft,
    getSimulator,
    checkEventParams,
    getEvent,
    checkOutStart,
    checkInStart,
    getDiscrepancy,
    getInstructorInfo,
    ...formActions
  },
  mergeActionsToProps
)(EventModals);
